import { useCallback, useRef, useState, type FormEvent, type ReactNode } from 'react'

import * as api from '../api/masterdata.api.js'
import { ApiError } from '../api/client.js'
import { DataTable, type Column } from '../components/DataTable.tsx'

const COLUMNS: readonly Column<api.Participant>[] = [
  { key: 'reg', label: 'No. Registrasi', render: (p) => <code>{p.registrationNo}</code> },
  { key: 'nama', label: 'Nama Lengkap', render: (p) => p.fullName },
  { key: 'inst', label: 'Institusi', render: (p) => p.institution?.name ?? '—' },
  {
    key: 'lahir',
    label: 'Tempat, Tgl Lahir',
    render: (p) =>
      [p.birthPlace, p.birthDate === null ? null : p.birthDate.slice(0, 10)]
        .filter((v) => v !== null)
        .join(', ') || '—',
  },
]

export function PesertaPage(): ReactNode {
  const [version, setVersion] = useState(0)
  const fetchPage = useCallback((page: number, q: string) => api.listParticipants(page, q), [])

  return (
    <div>
      <h1>Peserta</h1>
      <div className="page-actions">
        <TambahPeserta onDone={() => setVersion((v) => v + 1)} />
        <ImporCsv onDone={() => setVersion((v) => v + 1)} />
      </div>
      <DataTable columns={COLUMNS} fetchPage={fetchPage} version={version} />
    </div>
  )
}

function TambahPeserta({ onDone }: { onDone: () => void }): ReactNode {
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    setBusy(true)
    setError(null)
    try {
      await api.createParticipant({
        registrationNo: String(form.get('registrationNo') ?? ''),
        fullName: String(form.get('fullName') ?? ''),
        ...(form.get('birthPlace') ? { birthPlace: String(form.get('birthPlace')) } : {}),
        ...(form.get('birthDate') ? { birthDate: String(form.get('birthDate')) } : {}),
      })
      setOpen(false)
      onDone()
    } catch (err) {
      setError(
        err instanceof ApiError
          ? (err.details?.map((d) => `${d.field}: ${d.message}`).join('; ') ?? err.message)
          : 'Tidak dapat menghubungi server',
      )
    } finally {
      setBusy(false)
    }
  }

  if (!open) return <button onClick={() => setOpen(true)}>+ Tambah Peserta</button>

  return (
    <form className="inline-form" onSubmit={onSubmit}>
      <input name="registrationNo" placeholder="No. registrasi" required />
      <input name="fullName" placeholder="Nama lengkap" required />
      <input name="birthPlace" placeholder="Tempat lahir" />
      <input name="birthDate" type="date" />
      {error !== null && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      <div className="inline-form-actions">
        <button type="submit" disabled={busy}>
          Simpan
        </button>
        <button type="button" onClick={() => setOpen(false)} disabled={busy}>
          Batal
        </button>
      </div>
    </form>
  )
}

function ImporCsv({ onDone }: { onDone: () => void }): ReactNode {
  const fileRef = useRef<HTMLInputElement>(null)
  const [report, setReport] = useState<api.ImportReport | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function onPick(): Promise<void> {
    const file = fileRef.current?.files?.[0]
    if (file === undefined) return
    setBusy(true)
    setError(null)
    setReport(null)
    try {
      const text = await file.text()
      const result = await api.importParticipantsCsv(text)
      setReport(result)
      if (result.created > 0) onDone()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Tidak dapat menghubungi server')
    } finally {
      setBusy(false)
      if (fileRef.current !== null) fileRef.current.value = ''
    }
  }

  return (
    <div className="import-csv">
      <label className="import-label">
        Impor CSV
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          onChange={() => void onPick()}
          disabled={busy}
          hidden
        />
      </label>
      <a
        className="hint"
        download="template-peserta.csv"
        href={
          'data:text/csv;charset=utf-8,' +
          encodeURIComponent(
            'no_registrasi,nama_lengkap,kode_institusi,tempat_lahir,tanggal_lahir\n',
          )
        }
      >
        unduh templat
      </a>

      {error !== null && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {report !== null && (
        <div className="import-report">
          <p>
            <strong>{report.created}</strong> baris masuk, <strong>{report.failed}</strong> galat.
          </p>
          {report.errors.length > 0 && (
            <ul>
              {report.errors.slice(0, 20).map((e, i) => (
                <li key={i}>
                  Baris {e.row}, kolom <code>{e.field}</code>: {e.message}
                </li>
              ))}
              {report.errors.length > 20 && <li>… dan {report.errors.length - 20} galat lagi</li>}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
